/**
 * playerWalkAnim.ts — BETA 22.85 — directional walk animation for the player.
 *
 * Dylan hand-made a full directional walk set for Greg (bag-head chibi). This
 * module maps those frames to 8 movement directions with left/right + NW
 * MIRRORING (Dylan: "a lot of photos can just be flipped for the other
 * direction"), and picks the right frame per movement angle + walk phase.
 *
 * A character with NO entry in CHARACTER_WALKS keeps its single static sprite
 * (zero regression). Greg is the proven reference; other characters get their
 * own sets (Gemini-generated to match) as they're produced.
 *
 * LEAF MODULE — no imports from engine (avoids cycles). engine.ts owns the
 * frame image cache + preload + render.
 */

interface WalkDir {
  idle: string;      // standing frame for this facing
  steps: string[];   // 2 walk-cycle step frames, alternated while moving
}

/** Only the 4 NATIVE facings are stored; W/SW/NW/SE derive by mirror/fallback. */
interface WalkSet {
  down: WalkDir;   // S — front-facing (eye holes toward camera, +y)
  up: WalkDir;     // N — back-facing (-y)
  right: WalkDir;  // E — side profile (+x)
  ne: WalkDir;     // NE — up-right diagonal
}

const GREG = '/characters/greg_walk/';

export const CHARACTER_WALKS: Record<string, WalkSet> = {
  greg: {
    down:  { idle: GREG + 'greg_topdown_v4.png', steps: [GREG + 'greg_down_f2.png',    GREG + 'greg_down_f3.png'] },
    up:    { idle: GREG + 'greg_up_f2.png',       steps: [GREG + 'greg_up_f1.png',      GREG + 'greg_up_f3.png'] },
    right: { idle: GREG + 'greg_right_idle.png',  steps: [GREG + 'greg_right_step1.png', GREG + 'greg_right_step2.png'] },
    ne:    { idle: GREG + 'greg_ne_idle_v8.png',  steps: [GREG + 'greg_ne_step1.png',    GREG + 'greg_ne_step_final.png'] },
  },
};

export function hasWalkSet(charId: string): boolean {
  return !!CHARACTER_WALKS[charId];
}

/** Every distinct frame url for a character — engine preloads these. */
export function listWalkFrameUrls(charId: string): string[] {
  const w = CHARACTER_WALKS[charId];
  if (!w) return [];
  const out: string[] = [];
  for (const d of [w.down, w.up, w.right, w.ne]) { out.push(d.idle, ...d.steps); }
  return Array.from(new Set(out));
}

const STEP_DUR = 0.14; // seconds per walk-cycle step

/**
 * Resolve the walk frame for a movement angle (atan2(dy, dx), +y = down/south),
 * whether the player is moving, and a walk-phase accumulator (seconds). Returns
 * the frame url + whether to draw it horizontally MIRRORED (W/SW/NW). null if
 * the character has no walk set (caller falls back to the static sprite).
 */
export function resolveWalkFrame(
  charId: string,
  angle: number,
  moving: boolean,
  phase: number,
): { url: string; flip: boolean } | null {
  const w = CHARACTER_WALKS[charId];
  if (!w) return null;
  const P = Math.PI;
  let set: WalkDir;
  let flip = false;
  if (angle >= -P / 8 && angle < P / 8) { set = w.right; }               // E
  else if (angle >= P / 8 && angle < 3 * P / 8) { set = w.right; }        // SE → side profile
  else if (angle >= 3 * P / 8 && angle < 5 * P / 8) { set = w.down; }     // S — front
  else if (angle >= 5 * P / 8 && angle < 7 * P / 8) { set = w.right; flip = true; } // SW → mirrored side
  else if (angle >= 7 * P / 8 || angle < -7 * P / 8) { set = w.right; flip = true; } // W — mirrored side
  else if (angle >= -7 * P / 8 && angle < -5 * P / 8) { set = w.ne; flip = true; }   // NW → mirrored NE
  else if (angle >= -5 * P / 8 && angle < -3 * P / 8) { set = w.up; }     // N — back
  else { set = w.ne; }                                                    // NE

  if (!moving || set.steps.length === 0) return { url: set.idle, flip };
  const idx = Math.floor(phase / STEP_DUR) % set.steps.length;
  return { url: set.steps[idx], flip };
}
